import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';
import { getCurrencySymbol } from '../lib/currency';

function Payment() {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [customAmount, setCustomAmount] = useState(null);
  const [selectedBaseAmount, setSelectedBaseAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const { currency, setCurrency, formatFromEur, formatAmount, convertFromEur } = useCurrency();

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setCredits(parseFloat(parsedUser.credits) || 0);
    }
    fetchPackages();
    setPageMeta({
      title: 'Payments — Elorimi',
      description: 'Top up your Elorimi balance and manage payments.',
    });
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/api/payments/packages');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handlePackageSelect = (amount) => {
    setSelectedBaseAmount(amount);
    setCustomAmount(null);
    setCredits(convertFromEur(amount));
  };

  const handleCustomAmount = (value) => {
    setSelectedBaseAmount(null);
    setCredits(value);
    setCustomAmount(value);
  };

  const handlePayment = async () => {
    if (!user) {
      message.error('Please login first');
      return;
    }

    if (!credits || credits <= 0) {
      message.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
        const response = await axios.post('/api/payments/create-payment', {
          userId: user.id,
          amount: credits,
          currency,
          description: 'Elorimi Credits Top-up',
          frontOrigin: window.location.origin,
        });

      if (response.data.url) {
        // Redirect to payment page
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      message.error(error.response?.data?.error || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [5, 10, 25, 50, 100];

  useEffect(() => {
    if (selectedBaseAmount !== null && customAmount === null) {
      setCredits(convertFromEur(selectedBaseAmount));
    }
  }, [currency, selectedBaseAmount, customAmount, convertFromEur]);

  return (
    <div className="payment-section">
      {/* Animated Background */}
      <div className="bg-animation">
        <div className="bg-mesh"></div>
        <div className="bg-grid"></div>
        <div className="bg-glow bg-glow-1"></div>
        <div className="bg-glow bg-glow-2"></div>
        <div className="bg-glow bg-glow-3"></div>
      </div>

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="payment-card glass">
              <div className="payment-header">
                <h2 className="payment-title">
                  <i className="fas fa-coins"></i>
                  Add Credits
                </h2>
                <p className="payment-subtitle">
                  Current Balance: <span className="balance-amount">{formatAmount(credits)}</span>
                </p>
              </div>

              <div className="currency-switch">
                <span className="currency-label">Currency</span>
                <div className="currency-buttons">
                  {['EUR', 'GBP'].map((code) => (
                    <button
                      key={code}
                      className={`currency-btn ${currency === code ? 'active' : ''}`}
                      onClick={() => setCurrency(code)}
                      type="button"
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="quick-amounts">
                {quickAmounts.map((amount) => (
                  <button
                    key={amount}
                    className={`quick-amount-btn ${customAmount === null && selectedBaseAmount === amount ? 'active' : ''}`}
                    onClick={() => handlePackageSelect(amount)}
                  >
                    +{formatFromEur(amount)}
                  </button>
                ))}
              </div>

              {/* Custom Amount Input */}
              <div className="custom-amount-section">
                <label className="form-label">Or enter custom amount</label>
                <div className="custom-amount-input">
                  <span className="currency-symbol">{getCurrencySymbol(currency)}</span>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    value={customAmount || ''}
                    onChange={(e) => handleCustomAmount(parseFloat(e.target.value) || null)}
                  />
                </div>
              </div>

              {/* Selected Amount Display */}
              <div className="selected-amount">
                <span className="label">Amount to add:</span>
                <span className="value">{formatAmount(credits)}</span>
              </div>

              {/* Payment Button */}
              <button
                className="btn btn-primary btn-lg w-100 payment-btn"
                onClick={handlePayment}
                disabled={loading || !credits || credits <= 0}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-credit-card"></i>
                    Pay {formatAmount(credits)}
                  </>
                )}
              </button>

              {/* Security Note */}
              <div className="payment-security">
                <i className="fas fa-shield-alt"></i>
                <span>Secure payment powered by Paynet</span>
              </div>
            </div>
          </div>

          {/* Available Packages */}
          {packages.length > 0 && (
            <div className="col-lg-6">
              <div className="packages-card glass">
                <h3 className="packages-title">
                  <i className="fas fa-gift"></i>
                  Popular Packages
                </h3>
                <div className="packages-grid">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className={`package-item ${pkg.isPopular ? 'popular' : ''}`}
                      onClick={() => handlePackageSelect(parseFloat(pkg.price))}
                    >
                      {pkg.isPopular && (
                        <span className="package-badge">Popular</span>
                      )}
                      <div className="package-coins">
                        <i className="fas fa-coins"></i>
                        {pkg.coins} coins
                      </div>
                      <div className="package-price">
                        {formatFromEur(pkg.price)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .payment-section {
          padding: 120px 20px 60px;
          position: relative;
          min-height: 100vh;
        }

        .payment-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-xl);
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .payment-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .payment-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 0.5rem;
        }

        .payment-title i {
          color: var(--primary);
          margin-right: 0.5rem;
        }

        .payment-subtitle {
          color: var(--gray-600);
          font-size: 0.9rem;
        }

        .quick-amounts {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .currency-switch {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
          padding: 0.75rem 1rem;
          margin-bottom: 1.5rem;
        }

        .currency-label {
          color: var(--gray-600);
          font-size: 0.85rem;
        }

        .currency-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .currency-btn {
          padding: 0.35rem 0.75rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.04);
          color: var(--gray-600);
          font-size: 0.8rem;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .currency-btn.active {
          border-color: var(--primary);
          color: var(--primary);
          background: rgba(159, 241, 62, 0.08);
        }
.quick-amount-btn {
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
          color: var(--gray-600);
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .quick-amount-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(159, 241, 62, 0.3);
          color: var(--white);
        }

        .quick-amount-btn.active {
          background: rgba(159, 241, 62, 0.1);
          border-color: var(--primary);
          color: var(--primary);
        }

        .custom-amount-section {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          color: var(--gray-600);
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
        }

        .custom-amount-input {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
          padding: 0 1rem;
        }

        .custom-amount-input:focus-within {
          border-color: var(--primary);
        }

        .currency-symbol {
          color: var(--gray-600);
          font-size: 1.1rem;
          margin-right: 0.5rem;
        }

        .custom-amount-input input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 1rem 0;
          color: var(--white);
          font-size: 1.1rem;
          outline: none;
        }

        .custom-amount-input input::placeholder {
          color: var(--gray-700);
        }

        .selected-amount {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: rgba(159, 241, 62, 0.05);
          border: 1px solid rgba(159, 241, 62, 0.1);
          border-radius: var(--radius-lg);
          margin-bottom: 1.5rem;
        }

        .selected-amount .label {
          color: var(--gray-600);
          font-size: 0.9rem;
        }

        .selected-amount .value {
          font-family: 'Poppins', sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary);
        }

        .payment-btn {
          margin-bottom: 1.5rem;
        }

        .payment-security {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: var(--gray-600);
          font-size: 0.8rem;
        }

        .payment-security i {
          color: var(--primary);
        }

        .packages-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-xl);
          padding: 2rem;
        }

        .packages-title {
          font-family: 'Poppins', sans-serif;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--white);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .packages-title i {
          color: var(--primary);
          margin-right: 0.5rem;
        }

        .packages-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .package-item {
          position: relative;
          padding: 1.25rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: var(--radius-lg);
          text-align: center;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .package-item:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(159, 241, 62, 0.3);
          transform: translateY(-2px);
        }

        .package-item.popular {
          border-color: var(--primary);
          background: rgba(159, 241, 62, 0.03);
        }

        .package-badge {
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--primary), #7bc94d);
          color: #000;
          padding: 0.2rem 0.75rem;
          border-radius: 10px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .package-coins {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--white);
          margin-bottom: 0.5rem;
        }

        .package-coins i {
          color: var(--primary);
          margin-right: 0.5rem;
        }

        .package-price {
          font-size: 0.9rem;
          color: var(--gray-600);
        }

        @media (max-width: 992px) {
          .packages-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default Payment;






